import { Component, OnInit } from "@angular/core";
import { Location } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { AlertService, BaseComponentMode, BaseMaterialDatatableComponent, ConfirmDialogComponent, ApiListQueryParameter } from "@impacgroup/angular-baselib";
import { PostcodeUsersService } from "./postcodeusers.service";
import { TranslateService } from "@ngx-translate/core";
import { BsModalService } from "ngx-bootstrap/modal";
import { PostcodeAreaDetails, PostcodeAreaDetailsRow, UserDetails } from "src/app/api-models/PostcodeUser";
import { PermissionService } from "@impacgroup/angular-oauth-base";
import { PostcodeUsersMainComponent } from "./postcodeusers.main.component";
import { map } from "rxjs/operators";

@Component({
    selector: "app-postcodeuserdetail",
    templateUrl: "./postcodeuserdetail.component.html",
    styleUrls: ["postcodeuserdetail.component.scss"],
    standalone: false
})
export class PostcodeUserDetailComponent extends BaseMaterialDatatableComponent<PostcodeAreaDetails> {
    public canAddPostcodes = false;
    public canDeletePostcodes = false;
    public postcodeUser: UserDetails = null;

    public assignedPostcodes: PostcodeAreaDetails[] = [];
    public unassignedPostcodes: PostcodeAreaDetails[] = [];

    public UTCDATEFORMAT: string = "";
    private queryParams: ApiListQueryParameter;

    private userId: string = null;

    constructor(
        private route: ActivatedRoute,
        private permissionService: PermissionService,
        private postcodeUsersService: PostcodeUsersService,
        private router: Router,
        private alertService: AlertService,
        private translateService: TranslateService,
        private _location: Location,
        private modalService: BsModalService,
        parent: PostcodeUsersMainComponent
    ) {
        super({
            stateSaveConfig: {
                stateSaveMode: postcodeUsersService.datatableStateSaveMode,
                stateSaveComponent: parent,
                stateSaveStorageKey: "dtstate_postcodeusers_list",
            },
            sortState: {
                active: "postcodeStart",
                direction: "asc",
            },
            displayedColumns: ["actions", "postcodeStart", "postcodeEnd" ,"created", "updated"],
            listService: postcodeUsersService,
        });

        this.UTCDATEFORMAT = this.postcodeUsersService.UTCDATEFORMAT;

        this.subscriptions.push(
            this.route.params.subscribe((params) => {
                this.userId = params.id;
                if (this.userId) {
                    if (permissionService.hasPermission("POSTCODEUSERS.ADD") || permissionService.hasPermission("POSTCODEUSERS.DELETE")) {
                        this.initEditMode();
                    } else {
                        this.initViewMode();
                    }
                } else {
                    this._location.back();
                }
            })
        );
    }

    override loadData() {
        this.pageState.isLoading = true;

        // get api query parameters by datatable
        this.queryParams = {
            page: this.pageState.currentPage + 1,
            limit: this.pageState.pageSize,
            search: this.pageState.search,
            sort: `${this.pageState.sortState.active}|${this.pageState.sortState.direction}`,
        };

        const observable = this.postcodeUsersService.read(this.userId, this.queryParams).pipe(
            map((data) => {
                this.postcodeUser = data.userDetails;
                this.assignedPostcodes = data.assignedPostcodes ?? [];
                this.unassignedPostcodes = data.unassignedPostcodes ?? [];

                setTimeout(() => {
                    if (this.paginator) {
                        this.paginator.pageIndex = this.pageState.currentPage;
                        this.paginator.length = data.countUnassignedPostcodes;
                    }
                });

                return {
                    list: [],
                    count: data.countUnassignedPostcodes ?? 0,
                    total: data.totalUnassignedPostcodes ?? 0
                };
            })
        );

        this.subscriptions.push(
            observable.subscribe({
                next: (data) => {
                    this.pageState.isLoading = false;
                },
                error: (error) => {
                    console.error(error);
                    this.pageState.isLoading = false;
                },
            })
        );

        return observable;
    }

    initViewMode() {
        this.MODE = BaseComponentMode.VIEW;
    }

    initEditMode() {
        this.MODE = BaseComponentMode.EDIT;
        this.canAddPostcodes = this.permissionService.hasPermission("POSTCODEUSERS.ADD");
        this.canDeletePostcodes = this.permissionService.hasPermission("POSTCODEUSERS.DELETE");
    }

    back() {
        this._location.back();
    }

    changeUserCheckbox(event, postcode: PostcodeAreaDetailsRow) {
        const newCheckboxState = event.target.checked;
        // Prevent toggling checkbox until the modal is answered (or not)
        event.target.checked = !newCheckboxState;
        postcode.checked = !newCheckboxState;
        if (newCheckboxState === true) {
            this.assignUserTo(postcode);
        } else {
            this.unassignUserFrom(postcode);
        }
    }

    private unassignUserFrom(postcode: PostcodeAreaDetailsRow) {
        const initialState = {
            title: this.translateService.instant("postcodeusers.detail.assignedPostcodes.delete.title"),
            message: this.translateService.instant("postcodeusers.detail.assignedPostcodes.delete.message"),
            closeBtnName: this.translateService.instant("global.buttons.close"),
            confirmBtnName: this.translateService.instant("global.buttons.ok")
        };
        const bsModalRef = this.modalService.show(ConfirmDialogComponent, { initialState });
        bsModalRef.content.confirmCallback = () => {
            this.subscriptions.push(
                this.postcodeUsersService.delete(this.userId, postcode._id, this.queryParams).subscribe((result) => {
                    this.postcodeUser = result.userDetails;
                    this.assignedPostcodes = result.assignedPostcodes ?? [];
                    this.unassignedPostcodes = result.unassignedPostcodes ?? [];

                    this.alertService.addSuccess(this.translateService.instant("postcodeusers.detail.assignedPostcodes.delete.success"));
                })
            );
        };
    }

    private assignUserTo(postcode) {
        this.subscriptions.push(
            this.postcodeUsersService.create(this.userId, postcode._id, this.queryParams).subscribe((result) => {
                this.postcodeUser = result.userDetails;
                this.assignedPostcodes = result.assignedPostcodes ?? [];
                this.unassignedPostcodes = result.unassignedPostcodes ?? [];

                this.alertService.addSuccess(this.translateService.instant("postcodeusers.detail.assignedPostcodes.add.success"));
            })
        );
    }
}
